import { Controller, FormProvider, useFormContext } from "react-hook-form";
import { nameof } from "ts-simple-nameof";
import { InputTextarea } from "primereact/inputtextarea";
import { AppDialog } from "../../../../common/components/AppDialog/AppDialog";
import { DefaultButton } from "../../../../common/components/button/defaultButton/DefaultButton";
import { DefaultFormMultiSelectField } from "../../../../common/components/forms/inputs/dropdown/DefaultFormMultiSelectField";
import { useMassSmsDialog } from "./hooks/useMassSmsDialog";
import { IMassSmsFormModel } from "./models/massSmsFormModel";
import styles from "./MassSmsDialog.module.scss";

interface IMassSmsDialogProps {
  visible: boolean;
  onHide: () => void;
}

const SMS_MAX_LENGTH = 65;

const SmsMessageField = () => {
  const { control, watch, formState: { errors } } = useFormContext<IMassSmsFormModel>();
  const message = watch("message") ?? "";
  const charCount = message.length;
  return (
    <div className={styles.field}>
      <label className={styles.field_label}>
        Съобщение<span className={styles.field_required}>*</span>
      </label>
      <Controller
        name="message"
        control={control}
        render={({ field }) => (
          <InputTextarea
            name={field.name}
            value={field.value as string}
            onChange={field.onChange}
            onBlur={field.onBlur}
            rows={4}
            autoResize
            maxLength={SMS_MAX_LENGTH}
            placeholder="Въведете текст на SMS съобщението"
            className={styles.field_textarea}
          />
        )}
      />
      <span className={`${styles.field_char_count} ${charCount >= SMS_MAX_LENGTH ? styles.field_char_count_limit : ""}`}>
        {charCount}/{SMS_MAX_LENGTH}
      </span>
      {errors.message && (
        <span className={styles.field_error}>{errors.message.message}</span>
      )}
    </div>
  );
};

export const MassSmsDialog = ({ visible, onHide }: IMassSmsDialogProps) => {
  const { form, userOptions, isUsersLoading, isSending, selectedUserIds, handleSend } =
    useMassSmsDialog(onHide);

  const selectedCount = selectedUserIds?.length ?? 0;

  return (
    <AppDialog
      visible={visible}
      onHide={onHide}
      header="Изпрати масов SMS"
      style={{ width: "90vw", maxWidth: "680px" }}
    >
      <FormProvider {...form}>
        <form className={styles.form} onSubmit={handleSend}>
          <DefaultFormMultiSelectField
            name={nameof<IMassSmsFormModel>((x) => x.userIds)}
            label="Получатели"
            options={userOptions}
            isLoading={isUsersLoading}
            filter
            filterBy="name"
            includeId={false}
            required
            width="100%"
            emptyMessage="Няма намерени потребители с телефонен номер"
            virtualScrollerOptions={{ itemSize: 43 }}
          />

          <SmsMessageField />

          {selectedCount > 0 && (
            <div className={styles.disclaimer}>
              <span>
                Внимание: ще бъдат изпратени{" "}
                <strong>{selectedCount}</strong>{" "}
                {selectedCount === 1 ? "SMS" : "SMS-а"}.
              </span>
            </div>
          )}

          <div className={styles.footer}>
            <DefaultButton
              label="Отказ"
              design="outline"
              onClick={onHide}
              type="button"
              width="auto"
            />
            <DefaultButton
              label="Изпрати"
              design="default"
              type="submit"
              loading={isSending}
              disabled={selectedCount === 0}
              width="auto"
            />
          </div>
        </form>
      </FormProvider>
    </AppDialog>
  );
};
